import React, { useEffect, useRef, useState } from "react";
import { Box, Grid2, TextField, Typography, useTheme } from "@mui/material";
import FAQAccordion from "./FAQAccordion";


const faqDataGrouped = [
    {
        category: "User Management",
        questions: [
            {
                q: "How can I create a new user in the system?",
                a: "Navigate to the User Management module, select 'Create User', fill in required information, and assign roles as necessary."
            },
            {
                q: "Can I delete a user?",
                a: "Yes, you can delete a user by selecting the user from the User Management module and clicking on 'Delete User'. Ensure proper authorization is followed before deletion."
            },
            {
                q: "How do I assign roles and permissions to users?",
                a: "Select the user from the User Management module, choose 'Edit Role', and assign the appropriate role and permissions."
            },
            {
                q: "How are roles and permissions managed?",
                a: "Roles can be customized within the User Management module, with specific permissions set for each role to control user access."
            },
            {
                q: "Is there access control for different modules?",
                a: "Yes, access to modules is controlled based on user roles and permissions assigned during user setup."
            }
        ]
    },
    {
        category: "User Login",
        questions: [
            {
                q: "What is the first-time login process like?",
                a: "During first-time login, users are onboarded with a data validation and setup process to ensure accurate user information."
            },
            {
                q: "How is two-factor authentication (2FA) implemented?",
                a: "2FA can be enabled via email or SMS to enhance security during user login."
            },
            {
                q: "How do I recover my password if forgotten?",
                a: "Use the 'Forgot Password' feature to request a recovery email or answer security questions for password reset."
            }
        ]
    },
    {
        category: "Client Setup",
        questions: [
            {
                q: "How do I enter basic client information?",
                a: "Navigate to 'Client Setup', fill in fields like name, contact details, SPOC, and primary stakeholders."
            },
            {
                q: "Can I update client logos and theme colors?",
                a: "Yes, use the client management tools to upload logos and customize theme colors according to client requirements."
            },
            {
                q: "How are client KPIs displayed on the dashboard?",
                a: "Admin users can add and modify KPIs that are then displayed within the client-specific dashboard interface."
            }
        ]
    },
    {
        category: "Project Management Setup",
        questions: [
            {
                q: "How do I manage existing projects?",
                a: "Use the Project Management module to view, update, and manage project metadata such as name and duration."
            },
            {
                q: "Can I add new projects?",
                a: "Yes, simply navigate to the Project Management module and select 'Add New Project'."
            },
            {
                q: "How can I manage project stakeholders?",
                a: "Assign roles and permissions to stakeholders within projects to ensure proper access control."
            }
        ]
    },
    {
        category: "Data Management",
        questions: [
            {
                q: "What file types can be uploaded?",
                a: "Accept Excel files, Word documents, PDFs, images, and drawings for approval workflow processing."
            },
            {
                q: "How is Excel data validated?",
                a: "Excel files are validated against pre-defined templates before they are routed to approval workflows."
            }
        ]
    },
    {
        category: "Document Management",
        questions: [
            {
                q: "How do I organize and categorize documents?",
                a: "Use the Document Management module to categorize, add, edit, and apply versioning to files."
            },
            {
                q: "Can I search for documents efficiently?",
                a: "Yes, implement search filters within the Document Management module for efficient document retrieval."
            }
        ]
    },
    {
        category: "Custom Metrics",
        questions: [
            {
                q: "How do I configure metrics on the dashboard?",
                a: "Admin users can turn metrics on/off and set alerts for active metrics in the Custom Metrics interface."
            },
            {
                q: "Are there notifications for metric alerts?",
                a: "Yes, stakeholders receive notifications whenever metric alerts are triggered."
            }
        ]
    },
    {
        category: "Workflow",
        questions: [
            {
                q: "Can I create new workflows?",
                a: "Yes, new workflows can be developed and assigned to various entities like projects and users."
            },
            {
                q: "Are workflows secured?",
                a: "Security measures are in place to ensure safe and automated workflow management."
            }
        ]
    },
    {
        category: "Reports",
        questions: [
            {
                q: "How are reports generated and shared?",
                a: "Reports are automatically generated at pre-configured intervals and can be viewed, exported, or shared easily."
            },
            {
                q: "Is custom report generation possible?",
                a: "Yes, users can configure and generate custom reports through the Reports module interface."
            }
        ]
    },
    {
        category: "Help and FAQ",
        questions: [
            {
                q: "How accessible are the FAQs?",
                a: "The FAQ section is designed for easy navigation and readability to help users find answers quickly."
            },
            {
                q: "Can I provide feedback on FAQs?",
                a: "User feedback functionality is integrated to improve FAQ content and usability over time."
            }
        ]
    },
    {
        category: "Communications",
        questions: [
            {
                q: "What functionalities are available in email communication?",
                a: "Emails can be sent and received within the interface with robust security protocols for protection."
            },
            {
                q: "How does the P2P text-based chat work?",
                a: "Chat functionality allows for sending/receiving texts and displays chat history while providing real-time notifications."
            }
        ]
    },
    {
        category: "Integration",
        questions: [
            {
                q: "How are integrations with SAP designed?",
                a: "An architecture for SAP API connectivity fetches finance and procurement data while ensuring data consistency and error logging."
            },
            {
                q: "Is future third-party integration possible?",
                a: "Yes, the tool has an adaptable modular integration framework for future third-party solutions, ensuring seamless connectivity."
            }
        ]
    }
];


const FeatureList = () => {
    const theme = useTheme();
    const [search, setSearch] = useState("");

    // Filter the grouped data based on search
    const filteredData = faqDataGrouped
        .map((category) => ({
            ...category,
            questions: category.questions.filter((qa) =>
                qa.q.toLowerCase().includes(search.toLowerCase())
            ),
        }))
        .filter((category) => category.questions.length > 0);

    return (
        <>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontSize: '24px', fontWeight: '500', color: '#252525', mb: 2, lineHeight: "32px", letterSpacing: '-0.4px' }}>
                        FAQ for feature list
                    </Typography>
                </Box>
            </Grid2>
            <Grid2 container spacing={2} sx={{ mt: '20px' }}>
                <Grid2 size={12}>
                    <Box mb={4}>
                        <TextField
                            fullWidth
                            placeholder="Search"
                            variant="outlined"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{
                                background: "#fff",
                            }}
                        />
                    </Box>
                    <FAQAccordion faqDataGrouped={filteredData} />

                </Grid2>
            </Grid2>
        </>
    );
};

export default FeatureList;
